import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';

import { SET_PRIVACY_POLICY_DIALOGUE_OPEN, SET_TERMS_OF_USE_DIALOGUE_OPEN, SET_FEEDBACK_DIALOGUE_OPEN, SET_WELCOME_DIALOGUE_OPEN } from '../actions/types';
import { siteboardAction } from '../actions/defaultActions';
import {
  MessageSquareText,
  BookOpen,
  ShieldCheck,
  FileText,
  X,
  ChevronRight,
} from 'lucide-react';

const HelpFloater = ({ siteboardAction, onboardingCompleted }) => {
  const elementRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuHovered, setMenuHovered] = useState(false);

  // Close the dialogue when escape is pressed
  useEffect(() => {
    const onKeyUp = (event) => {
      if(event.key === 'Escape') { 
        setMenuOpen(false);
      }
    };

    window.addEventListener('keyup', onKeyUp);
    return () => { // remove listener when component unloads
      window.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  // Close the dialogue if mouse is clicked anywhere outside it
  // TODO: do this for touch devices too
  useEffect(() => {
    const onMouseDown = (event) => {
     try {
       let element = event.target;
       do {
         if(element === elementRef.current) return;
         //if(typeof element.className === 'string' && element.className.indexOf('activityLine') >= 0) return;
         element = element.parentNode;
       } while(element.tagName !== 'body' && element.tagName !== 'BODY');
       setMenuOpen(false);
     } catch(error) {}
    };

    window.addEventListener('mousedown', onMouseDown);
    return () => { // remove listener when component unloads
      window.removeEventListener('mousedown', onMouseDown);
    };
  }, []);

  return (
    <div
      ref={elementRef}
      className="fixed bottom-4 right-4 flex flex-col items-end"
    >
      {/* Help menu */}
      <div
        id="help-support-menu"
        aria-hidden={!menuOpen}
        className={`
          absolute bottom-[calc(100%+12px)] right-0
          w-[290px] max-w-[calc(100vw-32px)]
          origin-bottom-right
          overflow-hidden rounded-xl
          border border-[#62626a]
          bg-[#303036] text-white
          shadow-[0_12px_40px_rgba(0,0,0,0.45)]
          transition-all duration-200 ease-out
          ${
            menuOpen
              ? 'visible translate-y-0 scale-100 opacity-100'
              : 'invisible pointer-events-none translate-y-2 scale-95 opacity-0'
          }
        `}
      >
        {/* Minimal header */}
        <div className="px-5 pb-2 pt-4">
          <h3 className="text-sm font-semibold tracking-wide text-white">
            Help & Support
          </h3>
        </div>

        {/* Menu items */}
        <div className="flex flex-col gap-0.5 p-2">
          {/* Feedback */}
          {onboardingCompleted === true && (
            <button
              type="button"
              tabIndex={menuOpen ? 0 : -1}
              onClick={() => {
                siteboardAction(SET_FEEDBACK_DIALOGUE_OPEN, { isOpen: true });
                setMenuOpen(false);
              }}
              className="
                group flex w-full items-center gap-3
                rounded-lg px-3 py-3 text-left
                transition-colors duration-200
                hover:bg-[#484850]
                focus-visible:outline-none focus-visible:bg-[#484850]
              "
            >
              <MessageSquareText
                size={19}
                strokeWidth={1.8}
                className="shrink-0 text-[#f9a928]"
              />

              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white">
                  Send Feedback
                </div>
                <div className="mt-0.5 text-xs text-[#b7b7c0]">
                  Help us improve your experience
                </div>
              </div>

              <ChevronRight
                size={16}
                className="
                  shrink-0 text-[#9999a3]
                  transition-transform duration-200
                  group-hover:translate-x-0.5
                "
              />
            </button>
          )}

          {/* Welcome screen */}
          {onboardingCompleted === true && (
            <button
              type="button"
              tabIndex={menuOpen ? 0 : -1}
              onClick={() => {
                siteboardAction(SET_WELCOME_DIALOGUE_OPEN, { isOpen: true });
                setMenuOpen(false);
              }}
              className="
                group flex w-full items-center gap-3
                rounded-lg px-3 py-3 text-left
                transition-colors duration-200
                hover:bg-[#484850]
                focus-visible:outline-none focus-visible:bg-[#484850]
              "
            >
              <BookOpen
                size={19}
                strokeWidth={1.8}
                className="shrink-0 text-[#a7a7b0]"
              />

              <span className="flex-1 text-sm font-medium text-white/90">
                Welcome Screen
              </span>

              <ChevronRight
                size={16}
                className="
                  text-[#9999a3] transition-transform duration-200
                  group-hover:translate-x-0.5
                "
              />
            </button>
          )}

          {/* Privacy policy */}
          <button
            type="button"
            tabIndex={menuOpen ? 0 : -1}
            onClick={() => {
              siteboardAction(SET_PRIVACY_POLICY_DIALOGUE_OPEN, { isOpen: true });
              setMenuOpen(false);
            }}
            className="
              group flex w-full items-center gap-3
              rounded-lg px-3 py-3 text-left
              transition-colors duration-200
              hover:bg-[#484850]
              focus-visible:outline-none focus-visible:bg-[#484850]
            "
          >
            <ShieldCheck
              size={19}
              strokeWidth={1.8}
              className="shrink-0 text-[#a7a7b0]"
            />

            <span className="flex-1 text-sm font-medium text-white/90">
              Privacy Policy
            </span>

            <ChevronRight
              size={16}
              className="
                text-[#9999a3] transition-transform duration-200
                group-hover:translate-x-0.5
              "
            />
          </button>

          {/* Terms of use */}
          <button
            type="button"
            tabIndex={menuOpen ? 0 : -1}
            onClick={() => {
              siteboardAction(SET_TERMS_OF_USE_DIALOGUE_OPEN, { isOpen: true });
              setMenuOpen(false);
            }}
            className="
              group flex w-full items-center gap-3
              rounded-lg px-3 py-3 text-left
              transition-colors duration-200
              hover:bg-[#484850]
              focus-visible:outline-none focus-visible:bg-[#484850]
            "
          >
            <FileText
              size={19}
              strokeWidth={1.8}
              className="shrink-0 text-[#a7a7b0]"
            />

            <span className="flex-1 text-sm font-medium text-white/90">
              Terms of Use
            </span>

            <ChevronRight
              size={16}
              className="
                text-[#9999a3] transition-transform duration-200
                group-hover:translate-x-0.5
              "
            />
          </button>
        </div>
      </div>

      {/* Floating help button */}
      <button
        type="button"
        title={menuOpen ? 'Close Help Menu' : 'Help and Information'}
        aria-label={menuOpen ? 'Close help menu' : 'Open help menu'}
        aria-expanded={menuOpen}
        aria-controls="help-support-menu"
        onClick={() => setMenuOpen(!menuOpen)}
        onMouseEnter={() => setMenuHovered(true)}
        onMouseLeave={() => setMenuHovered(false)}
        className={`
          group relative flex h-11 w-11
          items-center justify-center
          rounded-full border
          shadow-[0_4px_18px_rgba(0,0,0,0.35)]
          transition-all duration-200
          hover:-translate-y-0.5
          active:scale-95
          focus-visible:outline-none
          focus-visible:ring-2 focus-visible:ring-[#f9a928]/50
          ${
            menuOpen
              ? 'border-[#f9a928] bg-[#f9a928] text-[#222]'
              : 'border-[#777780] bg-[#38383f] text-white hover:border-[#f9a928] hover:bg-[#484850]'
          }
        `}
      >
        {/* Question mark */}
        <span
          aria-hidden="true"
          className={`
            absolute text-[23px] font-semibold leading-none
            transition-all duration-200
            ${
              menuOpen
                ? 'rotate-90 scale-50 opacity-0'
                : 'rotate-0 scale-100 opacity-100'
            }
          `}
        >
          ?
        </span>

        {/* Close icon */}
        <X
          size={21}
          strokeWidth={2}
          className={`
            absolute transition-all duration-200
            ${
              menuOpen
                ? 'rotate-0 scale-100 opacity-100'
                : '-rotate-90 scale-50 opacity-0'
            }
          `}
        />
      </button>
    </div>
  );
}

const mapStateToProps = state => { return { // define redux state that will be passed in as props
}};
export default connect(mapStateToProps, {siteboardAction})(HelpFloater);
